/**
 * L2「配色」→ 一句提示词(V0,已核准事实 ③)。
 *
 * ═══ 为什么单帧路径只做提示词注入,这不是妥协 ═══════════════════════════════════
 * L2 的「双驱动」(AI 图块 DNA + PIL 矢量块配色)只在**长图**才有第二条腿 ——
 * 长图里 text/data 块是程序矢量渲染的,palette 能直接上色。
 * 而**单帧海报整张都是引擎画的、没有 PIL 矢量块**,`CreativeOverridesPayload`
 * (`posterService.ts`)也没有 styleTokens 通道。
 * 所以单帧路径上把 L2 渲成一句色板/质感描述、拼进 `style.tone`
 * (L1 tone 本来就是注入 prompt【风格】段的自由文本)**就是正确实现** ——
 * 零后端零 ai 改。★不要为它去改 payload schema。
 *
 * ═══ ★ 全部正面表述,一个否定词都不许有 ═════════════════════════════════════════
 * V28.3 踩过:否定名词会被模型**字面化反向触发**(写「不要出现人手」反而画出手)。
 * 这条由 `tokensToPrompt.test.ts` 扫描断言锁死,不靠自觉。
 *
 * 写法对齐 `styleOptions.ts` 的 tone 公式:中文具象 + 英文 anchor
 * (gpt-image-2 / 万相 对英文术语训练密度更高)。
 */
import type { Density, Lighting, Material, StyleTokens, StyleTone } from "../../longimage/model/canvas";

const MATERIAL_PHRASE: Record<Material, string> = {
  matte: "哑光平涂质感,matte finish",
  glossy: "高光通透质感,glossy sheen",
  frosted: "磨砂玻璃质感,frosted glassmorphism",
  metallic: "拉丝金属质感,brushed metal",
  natural: "自然材质纹理,natural texture",
};

const LIGHTING_PHRASE: Record<Lighting, string> = {
  studio: "棚拍定向主光、阴影干净,studio directional lighting",
  natural: "自然光、光线柔和真实,natural daylight",
  dramatic: "戏剧性强对比光、明暗层次分明,dramatic high-contrast lighting",
  soft: "柔和漫射光、过渡细腻,soft diffused lighting",
};

const TONE_PHRASE: Record<StyleTone, string> = {
  premium_minimal: "整体高级克制,premium minimal",
  vivid_promo: "整体鲜明有促销张力,vivid promotional",
  warm_life: "整体温暖生活感,warm lifestyle",
  tech_cool: "整体科技冷调,cool tech",
  fresh_clean: "整体清新干净,fresh and clean",
};

const DENSITY_PHRASE: Record<Density, string> = {
  compact: "信息密实、版面充实,information-dense layout",
  normal: "疏密均衡,balanced spacing",
  airy: "大量呼吸留白,airy generous whitespace",
};

/**
 * 渲成一句可直接拼进【风格】段的中文描述。
 *
 * 色值直接给 hex —— 出图模型对 hex 有直接响应,比"玫瑰金色"这类中文色名精确得多;
 * 而这些 hex 是**产物颜色**(§3.2.5),本来就该原样传给引擎。
 */
export function tokensToPrompt(tokens: StyleTokens): string {
  const { palette: p } = tokens;
  return [
    `配色以 ${p.primary} 为主色、${p.secondary} 为辅助色,${p.accent} 作小面积强调`,
    `文字色 ${p.ink}、次要信息 ${p.muted}、分隔线 ${p.line}、背景 ${p.bg}`,
    MATERIAL_PHRASE[tokens.material],
    LIGHTING_PHRASE[tokens.lighting],
    TONE_PHRASE[tokens.tone],
    DENSITY_PHRASE[tokens.density],
  ].join(";");
}

/**
 * 把 L2 描述接到 L1 tone 后面 —— 两层各说各的,不互相覆盖。
 *
 * @param tone L1 画风的 tone 文本;`null` = 自由发挥(此时只给 L2 那句,让 AI 其余自判)
 * @param tokens L2 配色;`null` = 没有配色可说 → 原样返回 L1
 */
export function composeStylePrompt(
  tone: string | null | undefined,
  tokens: StyleTokens | null | undefined
): string | null {
  const l2 = tokens ? tokensToPrompt(tokens) : null;
  if (!tone && !l2) return null;
  if (!l2) return tone ?? null;
  if (!tone) return l2;
  return `${tone};${l2}`;
}
